import { useEffect, useMemo, useState } from 'react';
import { Save, Lightbulb } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { ItemsTable } from '@/components/form/ItemsTable';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { useToast } from '@/components/ui/useToast';
import { createGrn, listAvailablePosForGrn } from '@/api/grn';
import { type Po, type PoItem } from '@/api/po';
import { apiError } from '@/lib/apiError';
import { useFieldErrors } from '@/lib/useFieldErrors';
import { formatINR, todayISO } from '@/lib/format';

interface Props {
  open: boolean;
  defaultPoId?: number | null;
  onClose: () => void;
}

interface LineState {
  poItemId: number;
  itemName: string;
  imageUrl?: string | null;
  barcode?: string | null;
  poQty: number;
  remaining: number;
  receivedQty: string;
  shortQty: number;
  unit: string;
  unitPrice: number;
  quality: 'OK' | 'Damaged' | 'Rejected';
  /** How the line arrived: loose units, or packed in boxes. */
  packMode: 'unit' | 'box';
  /**
   * Adaptive box entry — a single number is the qty-per-box (auto-split across
   * received); multiple comma-separated numbers are explicit boxes.
   * e.g. "5" or "500, 400".
   */
  boxInput: string;
}

/**
 * Split a received qty into boxes of `perBox` units — full boxes plus a final
 * smaller box for any remainder. e.g. (9, 5) → [5, 4]; (20, 5) → [5,5,5,5].
 */
function splitIntoBoxes(received: number, perBox: number): number[] {
  if (!Number.isFinite(perBox) || perBox <= 0 || received <= 0) return [];
  const full = Math.floor(received / perBox);
  const remainder = received - full * perBox;
  const boxes = Array.from({ length: full }, () => perBox);
  if (remainder > 0) boxes.push(remainder);
  return boxes;
}

/** Parse a manual "500, 400" box-qty string into positive integers. */
function parseBoxes(raw: string): number[] {
  return raw
    .split(/[,\s]+/)
    .map((s) => Math.floor(Number(s.trim())))
    .filter((n) => Number.isFinite(n) && n > 0);
}

/**
 * The final box quantities for a line. One number ⇒ qty-per-box (auto-split
 * across received). Multiple numbers ⇒ explicit boxes as typed.
 */
function computeBoxes(l: LineState): number[] {
  if (l.packMode !== 'box') return [];
  const nums = parseBoxes(l.boxInput);
  if (nums.length === 0) return [];
  if (nums.length === 1) return splitIntoBoxes(Number(l.receivedQty), nums[0]);
  return nums;
}

export function GrnFormModal({ open, defaultPoId, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fe = useFieldErrors<'poId' | 'vendorInvoiceNo'>();
  const [poId, setPoId] = useState<number | ''>('');
  const [grnDate, setGrnDate] = useState(todayISO());
  const [subject, setSubject] = useState('');
  const [vendorInvoiceNo, setVendorInvoiceNo] = useState('');
  const [vendorInvoiceDate, setVendorInvoiceDate] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [qualityRemarks, setQualityRemarks] = useState('');
  const [lines, setLines] = useState<LineState[]>([]);

  useEffect(() => {
    if (!open) return;
    setPoId(defaultPoId ?? '');
    setGrnDate(todayISO());
    setSubject('');
    setVendorInvoiceNo('');
    setVendorInvoiceDate('');
    setVehicleNo('');
    setQualityRemarks('');
    setLines([]);
  }, [open, defaultPoId]);

  // Open POs available for receipt. Served from the GRN module so receipt
  // staff don't need the (broader) `po` permission just to pick a PO.
  const { data: posResp } = useQuery({
    queryKey: ['grn-available-pos'],
    queryFn: () => listAvailablePosForGrn(),
    enabled: open,
    staleTime: 30_000,
  });
  const openPos: Po[] = useMemo(
    () =>
      (posResp?.data ?? [])
        // Hide POs that are fully received — nothing left to enter a GRN for.
        // pendingQty is undefined on older API responses; only exclude when we
        // explicitly know it's zero.
        .filter((p) => p.pendingQty == null || p.pendingQty > 0)
        .sort((a, b) => b.id - a.id),
    [posResp],
  );
  const poOptions = useMemo<SearchableOption[]>(
    () => openPos.map((p) => ({
      value: p.id,
      label: `${p.poNo} · ${p.vendor.name}`,
      sublabel: [p.subject, `₹${formatINR(p.grandTotal)}`, p.pendingQty != null && p.pendingQty > 0 ? `${p.pendingQty} pending` : null].filter(Boolean).join(' · '),
    })),
    [openPos],
  );

  // Look up the picked PO from the already-loaded picker list — avoids a
  // second network round-trip AND avoids needing the `po` permission just
  // to fetch details.
  const selectedPo: Po | undefined = useMemo(
    () => (poId ? (posResp?.data ?? []).find((p) => p.id === poId) : undefined),
    [poId, posResp],
  );

  useEffect(() => {
    if (!selectedPo) return;
    const po = selectedPo;
    // Skip items already fully received on prior GRNs. Each remaining line
    // starts with Received = 0 (the operator enters what actually arrived);
    // SHORT therefore starts at the full pending qty.
    // Backend attaches `receivedQty` (sum across all GRNs) to each PoItem.
    const pending = po.items
      .map((pi: PoItem) => {
        const poQty = Number(pi.qty);
        const alreadyReceived = Number(pi.receivedQty ?? 0);
        const remaining = Math.max(0, poQty - alreadyReceived);
        return {
          poItemId: pi.id!,
          itemName: pi.itemName,
          imageUrl: pi.imageUrl,
          barcode: pi.barcode,
          poQty,
          remaining,
          receivedQty: '0',
          shortQty: remaining,
          unit: pi.unit,
          unitPrice: Number(pi.unitPrice),
          quality: 'OK' as const,
          packMode: 'unit' as const,
          boxInput: '',
        };
      })
      .filter((l) => l.remaining > 0);
    setLines(pending);
    // Subject carries forward from the PO (still editable here).
    setSubject(po.subject ?? '');
  }, [selectedPo]);

  const total = useMemo(
    () => lines.reduce((acc, l) => acc + Number(l.receivedQty || 0) * l.unitPrice, 0),
    [lines],
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      createGrn({
        grnDate,
        subject: subject.trim(),
        poId: Number(poId),
        vendorInvoiceNo: vendorInvoiceNo.trim(),
        vendorInvoiceDate: vendorInvoiceDate || undefined,
        vehicleNo: vehicleNo.trim() || undefined,
        qualityRemarks: qualityRemarks.trim() || undefined,
        items: lines
          .filter((l) => Number(l.receivedQty) > 0)
          .map((l) => {
            const boxes = computeBoxes(l);
            return {
              poItemId: l.poItemId,
              receivedQty: Number(l.receivedQty),
              quality: l.quality,
              ...(boxes.length ? { boxes } : {}),
            };
          }),
      }),
    onSuccess: (grn) => {
      qc.invalidateQueries({ queryKey: ['grn'] });
      qc.invalidateQueries({ queryKey: ['po'] });
      toast.pushStep(`GRN ${grn.grnNo} recorded`, {
        body: 'Next step — scan items and confirm in Stock Entry.',
        href: `/purchase/stock?grn=${grn.id}`,
        nextLabel: 'Open Stock Entry',
        tag: 'grn-created',
      });
      onClose();
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Save failed'), 'error');
    },
  });

  function onSubmit() {
    if (!fe.validate({
      poId: !poId && 'Select a PO',
      vendorInvoiceNo: !vendorInvoiceNo.trim() && 'Vendor invoice number is required',
    })) return;
    const validLines = lines.filter((l) => Number(l.receivedQty) > 0);
    if (validLines.length === 0) {
      toast.push('Enter received qty for at least one item', 'error');
      return;
    }
    // Validate each in-boxes line's breakdown.
    for (const l of validLines) {
      if (l.packMode !== 'box') continue;
      const nums = parseBoxes(l.boxInput);
      if (nums.length === 0) {
        toast.push(`Enter a box qty for "${l.itemName}"`, 'error');
        return;
      }
      // Explicit multi-box lists can't exceed what was received.
      if (nums.length > 1 && nums.reduce((a, b) => a + b, 0) > Number(l.receivedQty)) {
        toast.push(`Box quantities exceed received qty for "${l.itemName}"`, 'error');
        return;
      }
    }
    saveMutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Goods Receipt Note"
      size="xl"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : (<span className="inline-flex items-center gap-1"><Save size={14} /> Save GRN</span>)}
          </Button>
        </>
      }
    >
      {/* Phone: PO full width, then Date | Vehicle, Invoice # | Invoice date, Subject, Remarks (order-* only below sm). */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 sm:gap-3">
        <FormGroup label="PO Reference" required error={fe.errors.poId} className="order-1 col-span-2 sm:order-none sm:col-span-1">
          {/* Searchable list that stays inside the window (a native select stretches to its longest PO). */}
          <SearchableSelect
            options={poOptions}
            value={poId}
            onChange={(v) => { setPoId(v === '' ? '' : Number(v)); fe.clear('poId'); }}
            placeholder="Select an open PO…"
            keepOrder
            className={fe.has('poId') ? 'rounded-lg ring-1 ring-danger' : undefined}
          />
        </FormGroup>
        <FormGroup label="Date" required className="order-2 sm:order-none">
          <Input type="date" value={grnDate} onChange={(e) => setGrnDate(e.target.value)} />
        </FormGroup>
        <FormGroup label="Subject" className="order-6 col-span-2 sm:order-none">
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Copied from the PO — optional"
            maxLength={200}
          />
        </FormGroup>
        <FormGroup label="Vendor Invoice #" required error={fe.errors.vendorInvoiceNo} className="order-4 sm:order-none">
          <Input
            invalid={!!fe.errors.vendorInvoiceNo}
            value={vendorInvoiceNo}
            onChange={(e) => setVendorInvoiceNo(e.target.value)}
            placeholder="e.g. INV/2026/0123"
          />
        </FormGroup>
        <FormGroup label="Vendor Invoice Date" className="order-5 sm:order-none">
          <Input
            type="date"
            value={vendorInvoiceDate}
            onChange={(e) => setVendorInvoiceDate(e.target.value)}
          />
        </FormGroup>
        <FormGroup label="Vehicle #" className="order-3 sm:order-none">
          <Input value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} placeholder="e.g. UK07XY1234" />
        </FormGroup>
        <FormGroup label="Quality Remarks" className="order-7 col-span-2 sm:order-none">
          <Textarea
            rows={2}
            value={qualityRemarks}
            onChange={(e) => setQualityRemarks(e.target.value)}
            placeholder="Any quality issues or notes"
          />
        </FormGroup>
      </div>

      {poId && !selectedPo && (
        <div className="mt-4 rounded-card border border-[var(--border-color)] bg-white/[0.02] p-4 text-center text-sm text-[var(--text-muted)]">
          Loading PO items…
        </div>
      )}

      {lines.length > 0 && (
        <ItemsTable<LineState>
          title="Received Items"
          rows={lines}
          onChange={setLines}
          newRow={() => lines[0]}
          columns={[
            { key: 'item', header: 'Item', cell: (l) => (
              <div className="flex items-center gap-2">
                <ItemThumb src={l.imageUrl} />
                <div className="min-w-0">
                  <div className="font-bold">{l.itemName}</div>
                  {l.barcode && <div className="text-[10px] font-normal text-[var(--text-muted)]">{l.barcode}</div>}
                </div>
              </div>
            ) },
            {
              key: 'poQty',
              mobileOrder: 1,
              mobileTile: true,
              header: 'PO / Pending',
              align: 'right',
              cell: (l) => (
                <div className="flex flex-col items-center leading-tight sm:items-end">
                  <span>{l.poQty} {l.unit}</span>
                  {l.remaining !== l.poQty && (
                    <span className="text-[10px] font-bold text-warning">{l.remaining} pending</span>
                  )}
                </div>
              ),
            },
            {
              key: 'received',
              mobileOrder: 4,
              header: 'Received',
              width: '120px',
              align: 'right',
              cell: (l, _i, update) => (
                <Input
                  type="number"
                  step="1"
                  value={l.receivedQty}
                  onChange={(e) => {
                    const v = Number(e.target.value || 0);
                    update({
                      receivedQty: e.target.value,
                      // Short is measured against what's still pending on this
                      // PO item, not the original PO qty — prior GRNs already
                      // covered the rest.
                      shortQty: Math.max(0, l.remaining - v),
                    });
                  }}
                  className="text-right"
                />
              ),
            },
            {
              key: 'packing',
              mobileOrder: 7,
              mobileFull: true,
              header: 'Received as',
              width: '180px',
              cell: (l, _i, update) => {
                const recv = Number(l.receivedQty || 0);
                const nums = parseBoxes(l.boxInput);
                const boxes = computeBoxes(l);
                const boxSum = boxes.reduce((a, b) => a + b, 0);
                // Only an explicit multi-box list can over/under-shoot received.
                const over = nums.length > 1 && nums.reduce((a, b) => a + b, 0) > recv;
                const loose = nums.length > 1 ? Math.max(0, recv - boxSum) : 0;
                return (
                  <div className="flex flex-col gap-1">
                    <Select
                      value={l.packMode}
                      onChange={(e) => update({ packMode: e.target.value as LineState['packMode'] })}
                    >
                      <option value="unit">Individual</option>
                      <option value="box">In boxes</option>
                    </Select>
                    {l.packMode === 'box' && (
                      <>
                        <Input
                          value={l.boxInput}
                          onChange={(e) => update({ boxInput: e.target.value })}
                          placeholder="Qty/box e.g. 5 — or 500, 400"
                          className={over ? 'border-danger' : ''}
                          title="One number = qty per box (auto-splits received). Multiple = explicit boxes."
                        />
                        {boxes.length > 0 && (
                          <span className={`text-[9px] leading-tight ${over ? 'font-bold text-danger' : 'text-[var(--text-muted)]'}`}>
                            {boxes.length} box{boxes.length > 1 ? 'es' : ''} ·{' '}
                            {boxes.length <= 4 ? boxes.join(' + ') : `Σ${boxSum}`}
                            {over ? ' — exceeds received' : loose > 0 ? ` · ${loose} loose` : ''}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                );
              },
            },
            {
              key: 'short',
              mobileOrder: 5,
              mobileTile: true,
              header: 'Short',
              align: 'right',
              cell: (l) => (
                <span className={l.shortQty > 0 ? 'font-extrabold text-warning' : 'text-[var(--text-muted)]'}>
                  {l.shortQty}
                </span>
              ),
            },
            {
              key: 'price',
              mobileOrder: 2,
              mobileTile: true,
              header: 'Unit ₹',
              align: 'right',
              cell: (l) => formatINR(l.unitPrice),
            },
            {
              key: 'lineTotal',
              mobileOrder: 3,
              mobileTile: true,
              header: 'Total',
              align: 'right',
              cell: (l) => (
                <span className="font-bold text-accent">
                  ₹{formatINR(Number(l.receivedQty || 0) * l.unitPrice)}
                </span>
              ),
            },
            {
              key: 'quality',
              mobileOrder: 6,
              header: 'Quality',
              width: '120px',
              cell: (l, _i, update) => (
                <Select
                  value={l.quality}
                  onChange={(e) => update({ quality: e.target.value as LineState['quality'] })}
                >
                  <option>OK</option>
                  <option>Damaged</option>
                  <option>Rejected</option>
                </Select>
              ),
            },
          ]}
          mobileLayout="grid3"
          totalLabel="GRN Total"
          totalValue={`₹${formatINR(total)}`}
        />
      )}

      <div className="mt-3 rounded-md border border-info/30 bg-info/[0.05] px-2.5 py-1.5 text-[10px] leading-snug text-[var(--text-secondary)] sm:px-3 sm:py-2 sm:text-xs sm:leading-normal">
        <Lightbulb size={13} className="mr-1 inline-block align-[-2px]" /> GRN saves in <strong>Pending</strong> status. After QC, go to <strong>Stock Entry</strong> and
        confirm — that&apos;s when warehouse stock updates. For small items received in boxes, set
        <strong> Received as → In boxes</strong> and type the box qty: <strong>one number</strong> (e.g. 5)
        is the qty-per-box and the received qty auto-splits (remainder in a smaller box), or list
        <strong> each box&apos;s qty</strong> (e.g. 500, 400). Each box prints one label and a single scan
        moves its whole quantity.
      </div>
    </Modal>
  );
}
